/*
 * The applicant's pages (Hiring v2, owner 2026-11-04), in the brand's colors like the client pages, English or Spanish:
 * the careers page (/apply/<brand>), short and made for a phone, and the page from our text to pick a phone call time
 * (/a/<token>). Answers that do not fit only flag the card; nobody is turned away by the form.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { textConsentWords, type ApplyBooking, type ApplyLang, type CareersPage } from "@steward/shared";
import { api } from "../api/client";
import { InputText } from "../components/fields";
import { Banner, Button, Skeleton } from "../components/parts";
import { errText } from "../components/work";
import { Branded, NotFound } from "./public/common";

const WORDS = {
  en: {
    title: "Join our team",
    lead: "It takes about 2 minutes. We text you back today.",
    name: "Your name",
    phone: "Phone number",
    email: "Email (if you have one)",
    zip: "ZIP code where you live",
    role: "What job",
    days: "Days you can work",
    ride: "Do you have a ride to work?",
    workOk: "Are you allowed to work in the US?",
    start: "When can you start?",
    starts: ["Right away", "In 2 weeks", "Later"],
    about: "Tell us about any cleaning or crew work (if any)",
    yes: "Yes",
    no: "No",
    send: "Send my application",
    other: "Español",
    thanks: "Thanks for applying!",
    thanksLead: "We'll be in touch soon.",
    pick: "Pick a time for a quick phone call",
    pickLead: (n: number) => `About ${String(n)} minutes. We call the number you gave us.`,
    booked: "You're all set!",
    bookedLead: "We'll call you",
    change: "Pick a different time",
    none: "No open times right now. We'll text you.",
    closed: "This link is closed. Call or text us if you have a question.",
    hi: (first: string) => `Hi ${first}`,
  },
  es: {
    title: "Trabaja con nosotros",
    lead: "Toma unos 2 minutos. Te escribimos hoy.",
    name: "Tu nombre",
    phone: "Número de teléfono",
    email: "Email (si tienes)",
    zip: "Código postal donde vives",
    role: "Qué trabajo",
    days: "Días que puedes trabajar",
    ride: "¿Tienes cómo llegar al trabajo?",
    workOk: "¿Puedes trabajar en Estados Unidos?",
    start: "¿Cuándo puedes empezar?",
    starts: ["Ya mismo", "En 2 semanas", "Más adelante"],
    about: "Cuéntanos de tu trabajo de limpieza o de cuadrilla (si tienes)",
    yes: "Sí",
    no: "No",
    send: "Enviar mi solicitud",
    other: "English",
    thanks: "¡Gracias por aplicar!",
    thanksLead: "Te escribimos pronto.",
    pick: "Escoge una hora para una llamada corta",
    pickLead: (n: number) => `Unos ${String(n)} minutos. Te llamamos al número que nos diste.`,
    booked: "¡Listo!",
    bookedLead: "Te llamamos",
    change: "Escoger otra hora",
    none: "No hay horas libres ahora. Te escribimos.",
    closed: "Este enlace está cerrado. Llámanos o escríbenos si tienes preguntas.",
    hi: (first: string) => `Hola ${first}`,
  },
};

const DAY_WORDS: Record<ApplyLang, string[]> = {
  en: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  es: ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"],
};

/** Yes or No as two buttons. */
function YesNo({
  label,
  value,
  onChange,
  lang,
}: {
  label: string;
  value: boolean | null;
  onChange: (v: boolean) => void;
  lang: ApplyLang;
}) {
  return (
    <div className="field">
      <span className="label">{label}</span>
      <div className="choice" role="radiogroup" aria-label={label}>
        {[true, false].map((v) => (
          <button key={String(v)} type="button" role="radio" aria-checked={value === v} onClick={() => onChange(v)}>
            <b>{v ? WORDS[lang].yes : WORDS[lang].no}</b>
          </button>
        ))}
      </div>
    </div>
  );
}

export function CareersPageView({
  slug,
  src,
  ref: refCode,
  lang: asked,
}: {
  slug: string;
  src?: string;
  ref?: string;
  lang?: string;
}) {
  const page = useQuery({ queryKey: ["careers", slug], queryFn: () => api<CareersPage>("GET", `public/careers/${slug}`) });
  const opened = useRef(Date.now());
  const [lang, setLang] = useState<ApplyLang>(asked === "es" ? "es" : "en");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [role, setRole] = useState<string | null>(null);
  const [days, setDays] = useState<number[]>([]);
  const [ride, setRide] = useState<boolean | null>(null);
  const [workOk, setWorkOk] = useState<boolean | null>(null);
  const [start, setStart] = useState(0);
  const [about, setAbout] = useState("");
  const [textOk, setTextOk] = useState(false);
  const [trap, setTrap] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  if (page.error) return <NotFound what="page" />;
  const d = page.data;
  if (!d)
    return (
      <Branded brand={null}>
        <Skeleton rows={6} />
      </Branded>
    );
  const w = WORDS[lang];
  const roles = d.roles;
  const picked = role ?? roles[0]?.id ?? null;
  const consent = textConsentWords(lang, d.brand.name);
  const send = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ ok: true; token: string | null }>("POST", `public/careers/${slug}`, {
        name: name.trim(),
        phone: phone.trim(),
        ...(email.trim() ? { email: email.trim() } : {}),
        zip: zip.trim(),
        ...(about.trim() ? { about: about.trim() } : {}),
        language: lang,
        ...(picked ? { role: picked } : {}),
        ...(days.length ? { days } : {}),
        ...(ride !== null ? { ride } : {}),
        ...(workOk !== null ? { workOk } : {}),
        start: WORDS.en.starts[start],
        textOk,
        ...(textOk ? { consentWords: consent } : {}),
        ...(src ? { src: src.slice(0, 40) } : {}),
        ...(refCode ? { ref: refCode.slice(0, 40) } : {}),
        website: trap,
        openedMs: Date.now() - opened.current,
      });
      if (r.token) window.location.assign(`/a/${r.token}`);
      else setDone(true);
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Branded brand={d.brand} title={`${w.title} | ${d.brand.name}`}>
      <div className="careers">
        <div className="careers-lang">
          <Button type="tertiary" size="small" onClick={() => setLang(lang === "en" ? "es" : "en")}>
            {w.other}
          </Button>
        </div>
        {done ? (
          <>
            <h1>{w.thanks}</h1>
            <p className="rv-lead">{w.thanksLead}</p>
          </>
        ) : (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              void send();
            }}
          >
            <h1>{w.title}</h1>
            <p className="rv-lead">{w.lead}</p>
            <InputText label={w.name} required autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
            <InputText
              label={w.phone}
              required
              type="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <InputText
              label={w.zip}
              required
              inputMode="numeric"
              autoComplete="postal-code"
              maxLength={5}
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
            />
            {roles.length > 1 ? (
              <div className="field">
                <span className="label">{w.role}</span>
                <div className="choice" role="radiogroup" aria-label={w.role}>
                  {roles.map((r) => (
                    <button key={r.id} type="button" role="radio" aria-checked={picked === r.id} onClick={() => setRole(r.id)}>
                      <b>{lang === "es" && r.nameEs ? r.nameEs : r.name}</b>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="field">
              <span className="label">{w.days}</span>
              <div className="chips">
                {DAY_WORDS[lang].map((label, i) => (
                  <button
                    key={label}
                    type="button"
                    className="chip"
                    aria-pressed={days.includes(i + 1)}
                    onClick={() => setDays(days.includes(i + 1) ? days.filter((x) => x !== i + 1) : [...days, i + 1])}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <YesNo label={w.ride} value={ride} onChange={setRide} lang={lang} />
            <YesNo label={w.workOk} value={workOk} onChange={setWorkOk} lang={lang} />
            <div className="field">
              <span className="label">{w.start}</span>
              <div className="choice" role="radiogroup" aria-label={w.start}>
                {w.starts.map((s, i) => (
                  <button key={s} type="button" role="radio" aria-checked={start === i} onClick={() => setStart(i)}>
                    <b>{s}</b>
                  </button>
                ))}
              </div>
            </div>
            <InputText
              label={w.email}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <label className="field">
              <span className="label">{w.about}</span>
              <textarea rows={3} maxLength={1000} value={about} onChange={(e) => setAbout(e.target.value)} />
            </label>
            <label className="careers-consent">
              <input type="checkbox" checked={textOk} onChange={(e) => setTextOk(e.target.checked)} />
              <span>{consent}</span>
            </label>
            <input
              className="careers-trap"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={trap}
              onChange={(e) => setTrap(e.target.value)}
            />
            {err ? <Banner type="error">{err}</Banner> : null}
            <Button size="large" fullWidth loading={busy} submit>
              {w.send}
            </Button>
          </form>
        )}
      </div>
    </Branded>
  );
}

/** The phone call times, grouped by day, in the applicant's language. */
export function ApplyBookingPage({ token }: { token: string }) {
  const qc = useQueryClient();
  const key = ["apply-booking", token];
  const b = useQuery({ queryKey: key, queryFn: () => api<ApplyBooking>("GET", `public/apply/${token}`) });
  const [changing, setChanging] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => setChanging(false), [b.data?.booked]);
  if (b.error) return <NotFound what="page" />;
  const d = b.data;
  if (!d)
    return (
      <Branded brand={null}>
        <Skeleton rows={4} />
      </Branded>
    );
  const w = WORDS[d.language];
  const locale = d.language === "es" ? "es-US" : "en-US";
  const dayOf = (iso: string) =>
    new Date(iso).toLocaleDateString(locale, { timeZone: d.timezone, weekday: "long", month: "short", day: "numeric" });
  const timeOf = (iso: string) =>
    new Date(iso).toLocaleTimeString("en-US", { timeZone: d.timezone, hour: "numeric", minute: "2-digit" });
  const groups = new Map<string, string[]>();
  for (const s of d.slots) groups.set(dayOf(s), [...(groups.get(dayOf(s)) ?? []), s]);
  const pick = async (at: string) => {
    setBusy(at);
    setErr(null);
    try {
      const next = await api<ApplyBooking>("POST", `public/apply/${token}`, { at });
      qc.setQueryData(key, next);
    } catch (e) {
      setErr(errText(e));
      void b.refetch();
    } finally {
      setBusy(null);
    }
  };
  return (
    <Branded brand={d.brand} title={d.brand.name}>
      <div className="careers">
        {d.closed ? (
          <>
            <h1>{w.hi(d.first)}</h1>
            <p className="rv-lead">{w.closed}</p>
          </>
        ) : d.booked && !changing ? (
          <>
            <h1>{w.booked}</h1>
            <p className="rv-lead">
              {w.bookedLead} {dayOf(d.booked)}, {timeOf(d.booked)}.
            </p>
            <Button type="secondary" onClick={() => setChanging(true)}>
              {w.change}
            </Button>
          </>
        ) : (
          <>
            <h1>{w.hi(d.first)}</h1>
            <p className="rv-lead">{w.pick}</p>
            <p className="muted">{w.pickLead(d.minutes)}</p>
            {err ? <Banner type="error">{err}</Banner> : null}
            {groups.size === 0 ? <p>{w.none}</p> : null}
            {[...groups].map(([day, times]) => (
              <div key={day} className="careers-day">
                <b>{day}</b>
                <div className="careers-times">
                  {times.map((t) => (
                    <Button key={t} type="secondary" loading={busy === t} onClick={() => void pick(t)}>
                      {timeOf(t)}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </Branded>
  );
}
